/**
 * AdjunctionsGenerateMonadsExplainer — how an adjunction `F ⊣ G` induces
 * both a monad `T = G ∘ F` on `C` and a comonad `W = F ∘ G` on `D`,
 * inheriting their unit/counit from `η` and `ε`.
 *
 * This is the single most-used corollary of the adjunction structure in
 * CSK: every PRECOMMIT monad comes from a "commit / discharge" adjunction,
 * every REQUIRE comonad comes from a "supply / consume" adjunction. The
 * explainer is the bridge between the categorical Adjunction card and
 * the Monad/Comonad cards downstream.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  AdjunctionToMonadDiagram,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-adjunctions-generate-monads",
  kind: "Categorical structure",
  title: "Adjunctions generate monads & comonads",
  subtitle: "T = G ∘ F on C   ·   W = F ∘ G on D",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Adjunction", href: "#explainer-adjunction", accent: ACCENT },
  { label: "Triangle identities", href: "#explainer-triangle-identities", accent: ACCENT },
  { label: "Monad", href: "#explainer-monad" },
  { label: "Comonad", href: "#explainer-comonad" },
  { label: "Bimonad", href: "#explainer-bimonad" },
  { label: "PRECOMMIT (W)", href: "#explainer-graded-monad", accent: ACCENT },
  { label: "REQUIRE (At)", href: "#explainer-comonad", accent: ACCENT },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      Every adjunction <MathInline>{tex`F \dashv G`}</MathInline> produces two reusable pieces of
      structure for free: a <strong>monad</strong> <MathInline>{tex`T = G \circ F`}</MathInline> on
      the left side and a <strong>comonad</strong> <MathInline>{tex`W = F \circ G`}</MathInline> on
      the right side. The unit and counit of the adjunction are exactly the unit/counit and
      multiplication/comultiplication of these (co)monads. CSK uses this construction to turn an
      audit-style "commit then observe" trade into a programmable effect that the type system tracks
      for you.
    </p>
  ),
  intuition: (
    <>
      <p>
        Recall the free / forgetful adjunction. You take a set of letters, build the free group on
        them, then forget the multiplication and look at the underlying set. Composing those two
        operations — first build, then forget — gives you a new operation on sets: "take a set,
        return the underlying set of the free group on it". That composite is the{" "}
        <em>list of formal words</em> functor; it is a monad. The unit shoves a letter into the list
        as a one-element word; the multiplication concatenates a list of lists.
      </p>
      <p>
        Run the composition the other way and you get a comonad on groups: "take a group, build a
        new group on the underlying letters, then forget back to a group" — a context-rich version
        of the original group. The counit extracts; the comultiplication exposes one more layer.
      </p>
      <p>
        Generalize. Whenever you have a "build" functor <MathInline>{tex`F`}</MathInline> with a
        "forget" right adjoint <MathInline>{tex`G`}</MathInline>, the composite{" "}
        <MathInline>{tex`G \circ F`}</MathInline> is the smallest effect that captures "build, then
        forget": a monad whose unit is the original adjunction's unit. The composite{" "}
        <MathInline>{tex`F \circ G`}</MathInline> is the smallest co-effect that captures "forget,
        then build": a comonad whose counit is the adjunction's counit.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`F : C \to D`}</MathInline> and{" "}
        <MathInline>{tex`G : D \to C`}</MathInline> form an adjunction{" "}
        <MathInline>{tex`F \dashv G`}</MathInline> with unit <MathInline>{tex`\eta`}</MathInline>{" "}
        and counit <MathInline>{tex`\varepsilon`}</MathInline>.
      </p>
      <AdjunctionToMonadDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        Define <MathInline>{tex`T = G \circ F`}</MathInline> on <MathInline>{tex`C`}</MathInline>.
        Then <MathInline>{tex`(T, \eta, \mu)`}</MathInline> is a monad with:
      </p>
      <ul>
        <li>
          <strong>Unit</strong> <MathInline>{tex`\eta : \mathrm{Id}_{C} \Rightarrow T`}</MathInline>{" "}
          — the adjunction's unit.
        </li>
        <li>
          <strong>Multiplication</strong>{" "}
          <MathInline>{tex`\mu = G \varepsilon F : T \circ T \Rightarrow T`}</MathInline> — the
          adjunction's counit, whiskered by <MathInline>{tex`G`}</MathInline> on the left and{" "}
          <MathInline>{tex`F`}</MathInline> on the right.
        </li>
      </ul>
      <p>
        Symmetrically, define <MathInline>{tex`W = F \circ G`}</MathInline> on{" "}
        <MathInline>{tex`D`}</MathInline>. Then{" "}
        <MathInline>{tex`(W, \varepsilon, \delta)`}</MathInline> is a comonad with:
      </p>
      <ul>
        <li>
          <strong>Counit</strong>{" "}
          <MathInline>{tex`\varepsilon : W \Rightarrow \mathrm{Id}_{D}`}</MathInline> — the
          adjunction's counit.
        </li>
        <li>
          <strong>Comultiplication</strong>{" "}
          <MathInline>{tex`\delta = F \eta G : W \Rightarrow W \circ W`}</MathInline> — the
          adjunction's unit, whiskered by <MathInline>{tex`F`}</MathInline> on the left and{" "}
          <MathInline>{tex`G`}</MathInline> on the right.
        </li>
      </ul>
      <p>
        The monad axioms (left/right unit, associativity) follow from the triangle identities; so do
        the comonad axioms by dualization.{" "}
        <em>
          Every adjunction yields a unique such monad and comonad — that is a theorem, not a
          convention.
        </em>
      </p>
      <p>
        Going back is harder but well-studied. The <strong>Kleisli</strong> and{" "}
        <strong>Eilenberg–Moore</strong> categories of a monad supply two canonical adjunctions that{" "}
        <em>reproduce</em> the monad — Kleisli is the initial choice, Eilenberg–Moore is the
        terminal choice. CSK chooses the construction (Kleisli, EM, or a custom resolution)
        according to which one carries the seam witnesses that matter for the proof.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>Almost every CSK effect originates from an adjunction. Three concrete examples:</p>
      <ul>
        <li>
          <code>PRECOMMIT</code> (W) is the graded monad{" "}
          <code>
            T<sub>κ</sub> = G ∘ F
          </code>{" "}
          from the "commit ⊣ discharge" adjunction. The grade <code>κ</code> records the
          precommitment payload; <code>μ</code> sequences two precommitments by combining their
          grades; the unit
          <code>η</code> issues the precommitment.
        </li>
        <li>
          <code>REQUIRE</code> (At) is the comonad <code>W = F ∘ G</code> from a "supply ⊣ consume"
          adjunction. The counit <code>ε</code> consumes a supplied capability; the comultiplication{" "}
          <code>δ</code> reveals one more layer of "what was supplied" without consuming it.
        </li>
        <li>
          <code>ICG-At</code> (the at-most-once Immunity Closure Guarantee) bundles{" "}
          <code>PRECOMMIT</code>'s monad with <code>REQUIRE</code>'s comonad into a <em>bimonad</em>
          . Coherence between them is exactly a distributive law between <code>T</code> and{" "}
          <code>W</code> — which exists iff the underlying adjunction descends.
        </li>
      </ul>
      <p>
        Read this as the bridge from theory to practice: when CSK says "a precommitment is a monadic
        effect" or "a requirement is a comonadic effect", the underlying record is{" "}
        <em>an adjunction</em>. The corresponding seam is a missing distributive law; the bridge is
        the witness that the distributive law factors through the adjunction's unit or counit.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Adjunction → monad/comonad construction; Kleisli and Eilenberg–Moore resolutions.",
    },
    {
      label: "MonadFromAdjunction (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/MonadFromAdjunction.agda",
      note: "Mechanized construction: T = G ∘ F with μ = G ε F; monad axioms from triangle identities.",
    },
    {
      label: "Comonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/Comonad.agda",
      note: "Comonad axioms; the dual W = F ∘ G with δ = F η G is recovered by duality transport (see GradedMonadComonadDuality).",
    },
    {
      label: "Monad/Comonad duality transport (Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedMonadComonadDuality.agda",
      note: "The dualization that lifts ComonadFromAdjunction from MonadFromAdjunction.",
    },
    {
      label: "Kleisli vs Eilenberg–Moore comparison (Agda)",
      path: "agda/Foundation/TwoCatAdjunctions/KleisliEMComparison.agda",
      note: "The two canonical adjunctions resolving a monad; the choice carries seam witnesses.",
    },
    {
      label: "Bimonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedBimonad.agda",
      note: "Where the monad/comonad pair meets in CSK's ICG-At envelope.",
    },
  ],
};

export function AdjunctionsGenerateMonadsExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<AdjunctionToMonadDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
